import { LocationOn, Visibility, Search, CheckCircle, Cancel } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, FormControl, Grid, InputAdornment, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import Loader from '../../components/Loader/Loader';
import Toptag from '../../components/toptag/Toptag';
import apiFunctions from '../../api/apiFunctions';
import { formatMonDayYear } from '../../utils/formatDate';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: "24px",
    borderRadius: "12px",
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    transition: "box-shadow 0.3s ease",
    '&:hover': {
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
    }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "white",
    maxHeight: "calc(100vh - 300px)",
    '& .MuiTableHead-root': {
        backgroundColor: "#f5f7fa",
        '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: "14px",
            color: "#374151",
            borderBottom: "2px solid #e5e7eb"
        }
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            transition: "background-color 0.2s ease",
            '&:hover': {
                backgroundColor: "#f9fafb"
            },
            '&:nth-of-type(even)': {
                backgroundColor: "#fafbfc"
            }
        },
        '& .MuiTableCell-root': {
            fontSize: "14px",
            color: "#4b5563",
            borderBottom: "1px solid #e5e7eb",
            padding: "16px"
        }
    }
}));

const StatCard = styled(Paper)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    textAlign: "center",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    '&:hover': {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
    }
}));

export default function SlotRewardsViewer() {
    const [rewards, setRewards] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchKey, setSearchKey] = useState("");
    const [openMapDialog, setOpenMapDialog] = useState(false);
    const [selectedReward, setSelectedReward] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const slotId = location.state?.slotId || location.search?.split('slotId=')[1];

    useEffect(() => {
        if (slotId) {
            getAll();
        } else {
            toast.error("Slot ID is required", {
                position: toast.POSITION.TOP_RIGHT
            });
            navigate(-1);
        }
    }, [slotId]);

    const getAll = async () => {
        setIsLoading(true);
        try {
            let result = await apiFunctions.getSlotRewards(slotId);
            if (!result.data.status) {
                if (result.data.code === 401) {
                    localStorage.clear();
                    navigate("/login");
                    toast.info("Session Expired Please login again!!");
                }
                toast.error(result.data.message, {
                    position: toast.POSITION.TOP_RIGHT
                });
            } else {
                setRewards(result.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching slot rewards:', error);
            toast.error("Error fetching slot rewards", {
                position: toast.POSITION.TOP_RIGHT
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewMap = (reward) => {
        setSelectedReward(reward);
        setOpenMapDialog(true);
    };

    const handleCloseMap = () => {
        setOpenMapDialog(false);
        setSelectedReward(null);
    };

    const filteredRewards = rewards.filter(reward => {
        if (!searchKey) return true;
        const searchLower = searchKey.toLowerCase();
        return (
            reward.user_id?.user_name?.toLowerCase().includes(searchLower) ||
            reward.user_id?.email?.toLowerCase().includes(searchLower) ||
            reward.event_id?.title?.toLowerCase().includes(searchLower)
        );
    });

    const totalAmount = filteredRewards.reduce((sum, r) => sum + (r.reward_amount || 0), 0);
    const totalUsers = filteredRewards.length;
    const finalizedCount = filteredRewards.filter(r => r.status === 'finalized').length;

    const getGPSLink = (gps) => {
        if (!gps || !gps.coordinates || gps.coordinates.length < 2) return null;
        const [lng, lat] = gps.coordinates;
        return `https://www.google.com/maps?q=${lat},${lng}`;
    };

    return (
        <Box sx={{ padding: "20px", backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
            <Toptag name="Slot Rewards Viewer" />
            <Grid container spacing={3} style={{ marginTop: "10px" }}>
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 2 }}>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#3b82f6", fontWeight: 600, marginBottom: 1 }}>
                                {totalUsers}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Total Users</Typography>
                        </StatCard>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#10b981", fontWeight: 600, marginBottom: 1 }}>
                                {totalAmount.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Total Reward Amount</Typography>
                        </StatCard>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#06b6d4", fontWeight: 600, marginBottom: 1 }}>
                                {finalizedCount}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Finalized</Typography>
                        </StatCard>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#f59e0b", fontWeight: 600, marginBottom: 1 }}>
                                {filteredRewards.length - finalizedCount}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Pending</Typography>
                        </StatCard>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <StyledPaper>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                            <Typography variant="h6" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                Slot Rewards ({filteredRewards.length} total)
                            </Typography>
                            <TextField
                                size="small"
                                placeholder="Search by name, email, or event..."
                                value={searchKey}
                                onChange={(e) => setSearchKey(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search style={{ color: "#6b7280" }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: "300px",
                                    backgroundColor: "white",
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: "8px"
                                    }
                                }}
                            />
                        </Box>

                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                                <Loader />
                            </Box>
                        ) : (
                            <>
                                <StyledTableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>User Name</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Event</TableCell>
                                                <TableCell>Currency</TableCell>
                                                <TableCell align="right">Reward Amount</TableCell>
                                                <TableCell align="right">Rate/Hour</TableCell>
                                                <TableCell align="center">Duration</TableCell>
                                                <TableCell align="center">Attendance</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Reward Date</TableCell>
                                                <TableCell align="center">GPS</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredRewards.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={11} align="center" sx={{ padding: 4 }}>
                                                        <Typography variant="body1" color="textSecondary">
                                                            No rewards found for this slot
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                filteredRewards.map((reward) => (
                                                    <TableRow key={reward._id}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {reward.user_id?.user_name || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ color: "#6b7280" }}>
                                                                {reward.user_id?.email || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {reward.event_id?.title || '-'}
                                                            </Typography>
                                                            {reward.event_id?.place && (
                                                                <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                                                                    {reward.event_id.place}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {reward.currency || 'USDC'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#10b981" }}>
                                                                {reward.reward_amount?.toFixed(2) || '0.00'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body2">
                                                                {reward.amount_per_hour_rate?.toFixed(2) || '0.00'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={`${reward.duration_hours || 0}h`}
                                                                size="small"
                                                                sx={{ backgroundColor: "#eff6ff", color: "#2563eb" }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {reward.attendance_count || 0}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={reward.status || 'pending'}
                                                                color={reward.status === 'finalized' ? 'success' : 'warning'}
                                                                size="small"
                                                                sx={{ fontWeight: 500 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {reward.reward_date ? formatMonDayYear(reward.reward_date) : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {reward.gps && reward.gps.coordinates && reward.gps.coordinates.length >= 2 ? (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<LocationOn />}
                                                                    onClick={() => handleViewMap(reward)}
                                                                    sx={{
                                                                        borderRadius: "6px",
                                                                        textTransform: "none",
                                                                        fontSize: "12px"
                                                                    }}
                                                                >
                                                                    View
                                                                </Button>
                                                            ) : (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    N/A
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </StyledTableContainer>
                            </>
                        )}
                    </StyledPaper>
                </Grid>
            </Grid>

            <Dialog open={openMapDialog} onClose={handleCloseMap} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h6">GPS Location</Typography>
                        <Button onClick={handleCloseMap} size="small">Close</Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedReward && selectedReward.gps && selectedReward.gps.coordinates && selectedReward.gps.coordinates.length >= 2 ? (
                        <Box>
                            <Typography variant="body2" sx={{ marginBottom: 2, color: "#6b7280" }}>
                                <strong>User:</strong> {selectedReward.user_id?.user_name || '-'} ({selectedReward.user_id?.email || '-'})
                            </Typography>
                            <Typography variant="body2" sx={{ marginBottom: 2, color: "#6b7280" }}>
                                <strong>Coordinates:</strong> {selectedReward.gps.coordinates[1]?.toFixed(6)}, {selectedReward.gps.coordinates[0]?.toFixed(6)}
                            </Typography>
                            <Box sx={{ marginTop: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    href={getGPSLink(selectedReward.gps)}
                                    target="_blank"
                                    startIcon={<LocationOn />}
                                >
                                    Open in Google Maps
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            GPS coordinates not available
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            <ToastContainer />
        </Box>
    );
}
